import AddIcon from '@mui/icons-material/Add';
import { Button, CircularProgress, SxProps, Theme, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import { SearchInput } from '~/components/SearchInput';
import { ROUTES } from '~/constants/routes';
import { useNotification } from '~/hooks/useNotification';
import { subroundInitialValue } from '~/pages/CreateCompany';
import {
  useGetPitchbookCompanyMutation,
  useLazyGetPitchbookCompanyDataByDealIdsQuery,
  useLazyGetPitchbookCompanyDataQuery,
  useLazyGetPitchbookCompanyRawDealsQuery,
  useSearchPitchbookCompanyQuery,
} from '~/services/rtkQuery/company';
import {
  GetPitchbookCompanyDataResponse,
  GetPitchbookCompanyDataSeriesItem,
  GetPitchbookCompanyRawDealsResponse,
  GetPitchbookCompanyResponse,
  SearchPitchbookCompanyResponse,
} from '~/services/rtkQuery/company/types';
import { CreateCompanyFormValues } from '~/types/formValues';
import { DeepPartial } from '~/types/global';
import { CompanyStatus } from '~/types/models';
import { calculateAcquired } from '~/utils/calculations';

import { Companies } from './components/Companies';
import { CompanyModal } from './components/CompanyModal';
import { MatchedRawDeals, MatchRoundsModal } from './components/MatchRoundsModal';
import styles from './styles.module.scss';

const FETCH_LIMIT = 10;
const SEARCH_INPUT_SX: SxProps<Theme> = { width: '344px' };

export const PitchbookPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification, handleApiError } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams.get('search') || '';
  const currentPage = +(searchParams.get('page') || 1);
  const [selectedCompany, setSelectedCompany] = useState<
    SearchPitchbookCompanyResponse['result']['data'][number] | null
  >(null);
  const [selectedCompanyPitchbookData, setSelectedCompanyPitchbookData] = useState<
    GetPitchbookCompanyResponse['result']['data'] | null
  >(null);
  const [selectedCompanyRawDeals, setSelectedCompanyRawDeals] = useState<
    GetPitchbookCompanyRawDealsResponse['result']['data']
  >([]);
  const [selectedCompanyMatchedRawDeals, setSelectedCompanyMatchedRawDeals] =
    useState<MatchedRawDeals | null>(null);
  const [companyModalVisible, setCompanyModalVisible] = useState(false);
  const [matchRoundsModalVisible, setMatchRoundsModalVisible] = useState(false);
  const searchPitchbookCompanyQuery = useSearchPitchbookCompanyQuery(
    { query: searchValue.trim(), perPage: FETCH_LIMIT, pageNum: currentPage - 1 },
    { skip: searchValue.trim().length < 3 },
  );
  const [getPitchbookCompanyMutation, { isLoading: isLoadingGetPitchbookCompanyMutation }] =
    useGetPitchbookCompanyMutation();
  const [getPitchbookCompanyDataQuery, { isFetching: isFetchingGetPitchbookCompanyDataQuery }] =
    useLazyGetPitchbookCompanyDataQuery();
  const [
    getPitchbookCompanyDataByDealIdsQuery,
    { isFetching: isFetchingGetPitchbookCompanyDataByDealIdsQuery },
  ] = useLazyGetPitchbookCompanyDataByDealIdsQuery();
  const [
    getPitchbookCompanyRawDealsQuery,
    { isFetching: isFetchingGetPitchbookCompanyRawDealsQuery },
  ] = useLazyGetPitchbookCompanyRawDealsQuery();

  useEffect(() => {
    (async () => {
      try {
        if (selectedCompany) {
          const data = await getPitchbookCompanyMutation({
            pbId: selectedCompany.pbId,
            source: selectedCompany.source,
          }).unwrap();

          setSelectedCompanyPitchbookData(data.result.data);
        }
      } catch (e) {
        handleApiError(e);
      }
    })();
  }, [getPitchbookCompanyMutation, handleApiError, selectedCompany]);

  const handleAddCompanyManually = useCallback(() => {
    navigate(ROUTES.CREATE_COMPANY);
  }, [navigate]);

  const handleSearchValueChange = useCallback(
    (value: string) => {
      setSearchParams(prevValue => {
        prevValue.set('search', value);
        prevValue.set('page', '1');

        return prevValue;
      });
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams(prevValue => {
        prevValue.set('page', page.toString());

        return prevValue;
      });
    },
    [setSearchParams],
  );

  const showCompanyModal = useCallback(
    (
      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
      company: SearchPitchbookCompanyResponse['result']['data'][number],
    ) => {
      // @ts-expect-error tagName exists
      if (event.target.tagName !== 'A') {
        setSelectedCompany(company);

        setCompanyModalVisible(true);
      }
    },
    [],
  );

  const hideCompanyModal = useCallback(() => {
    setSelectedCompany(null);
    setSelectedCompanyRawDeals([]);
    setSelectedCompanyMatchedRawDeals(null);

    setCompanyModalVisible(false);
  }, []);

  const handleCompanyProfileView = useCallback(
    (id: number, status: CompanyStatus | null) => {
      navigate(
        generatePath(status === 'draft' ? ROUTES.EDIT_COMPANY : ROUTES.COMPANY, {
          id: id.toString(),
        }),
      );
    },
    [navigate],
  );

  const showMatchRoundsModal = useCallback(async () => {
    try {
      if (selectedCompany) {
        const data = await getPitchbookCompanyRawDealsQuery({
          pbId: selectedCompany.pbId,
        }).unwrap();

        if (data.result.data.length) {
          setCompanyModalVisible(false);

          setSelectedCompanyRawDeals(data.result.data);

          setMatchRoundsModalVisible(true);
        } else {
          showNotification({
            type: 'info',
            title: 'Match Rounds',
            message: 'No previous rounds are available',
          });
        }
      }
    } catch (e) {
      handleApiError(e);
    }
  }, [getPitchbookCompanyRawDealsQuery, handleApiError, selectedCompany, showNotification]);

  const hideMatchRoundsModal = useCallback(() => {
    setMatchRoundsModalVisible(false);

    setCompanyModalVisible(true);
  }, []);

  const handleMatchRoundsSave = useCallback(
    (data: MatchedRawDeals) => {
      setSelectedCompanyMatchedRawDeals(data);

      hideMatchRoundsModal();
    },
    [hideMatchRoundsModal],
  );

  const getRoundSubrounds = useCallback(
    (
      seriesItems: GetPitchbookCompanyDataSeriesItem[],
      firstRound: boolean,
    ): CreateCompanyFormValues['form']['rounds'][number]['subrounds'] => {
      return seriesItems.map(data => {
        const acquired = calculateAcquired(data.dealsize || 0, data.postvaluation || 0);

        return {
          id: uuidv4(),
          dealDate: data.dealdate ? new Date(data.dealdate) : null,
          announcedDate: data.announceddate ? new Date(data.announceddate) : null,
          dealSize: data.dealsize ? data.dealsize.toFixed(2) : '',
          securityType: null,
          cap: '',
          discount: '',
          interest: '',
          conversionAmount: '',
          preMoneyValuation: data.premoneyvaluation ? data.premoneyvaluation.toFixed(2) : '',
          postMoneyValuation: data.postvaluation ? data.postvaluation.toFixed(2) : '',
          acquiredPercent: data.percentacquired || '',
          acquiredCalc: acquired.toString(),
          employees: data.employees ? data.employees.toString() : '',
          ceo: {
            name: data.ceo || '',
            education: null,
            biography: data.ceobiography || '',
            educationHistory: data.ceoeducationhistory || '',
          },
          ceoIsFounder: data.ceo_is_founder || false,
          ceoChanged: !!(!firstRound && data.ceo),
          generatingRevenue: data.generatingrevenue || false,
          participatingPreferred: data.participatingpreferred || false,
          noAnnouncement: !data.announceddate,
          revenueStatusChanged: false,
          generatingProfit: data.generatingprofit || false,
          investors: (data.investors || []).map(investor => ({
            id: investor.investorid || investor.investorname || '',
            pbId: investor.pbId || null,
            name: investor.investorname || '',
            lead: investor.isleadinvestor === 'Yes',
            new: investor.investorstatus === 'New Investor',
            corporate: false,
          })),
        };
      });
    },
    [],
  );

  const handleCompanyLoad = useCallback(async () => {
    try {
      if (selectedCompany) {
        let data: GetPitchbookCompanyDataResponse;

        if (selectedCompanyMatchedRawDeals) {
          data = await getPitchbookCompanyDataByDealIdsQuery({
            pbId: selectedCompany.pbId,
            series_a: selectedCompanyMatchedRawDeals['A'].map(rawDeal => rawDeal.dealid),
            series_b: selectedCompanyMatchedRawDeals['B'].map(rawDeal => rawDeal.dealid),
            series_c: selectedCompanyMatchedRawDeals['C'].map(rawDeal => rawDeal.dealid),
          }).unwrap();
        } else {
          data = await getPitchbookCompanyDataQuery({
            source: selectedCompany.source,
            pbId: selectedCompany.pbId,
          }).unwrap();
        }

        const roundASeriesItems = data.result.data.series_a || [];
        const roundBSeriesItems = data.result.data.series_b || [];
        const roundCSeriesItems = data.result.data.series_c || [];

        const normalizedData: DeepPartial<CreateCompanyFormValues> = {
          form: {
            name: data.result.data.companyname || '',
            formerName: data.result.data.companyformername || '',
            web: data.result.data.website || '',
            pitchbookUrl: selectedCompany.pitchBookProfileLink,
            foundedYear: data.result.data.yearfounded
              ? data.result.data.yearfounded.toString()
              : '',
            industries: (data.result.data.verticals || []).map(vertical => ({
              value: vertical.id,
              label: vertical.name,
            })),
            firstFinancingDate: data.result.data.firstfinancingdate
              ? new Date(data.result.data.firstfinancingdate)
              : undefined,
            firstFinancingDealType: data.result.data.firstfinancingdealtype
              ? {
                  label: data.result.data.firstfinancingdealtype.name,
                  value: data.result.data.firstfinancingdealtype.id,
                }
              : undefined,
            bio: data.result.data.description || '',
            competitors: (data.result.data.competitors || []).map(competitor => ({
              value: competitor.competitorid || competitor.competitorname,
              label: competitor.competitorname,
              meta: competitor.pbId ? { pbId: competitor.pbId } : undefined,
            })),
            rounds: [
              {
                name: 'A',
                subrounds: roundASeriesItems.length
                  ? getRoundSubrounds(roundASeriesItems, true)
                  : [{ ...subroundInitialValue, id: uuidv4() }],
              },
              {
                name: 'B',
                subrounds: roundBSeriesItems.length
                  ? getRoundSubrounds(roundBSeriesItems, false)
                  : [{ ...subroundInitialValue, id: uuidv4() }],
              },
              {
                name: 'C',
                subrounds: roundCSeriesItems.length
                  ? getRoundSubrounds(roundCSeriesItems, false)
                  : [{ ...subroundInitialValue, id: uuidv4() }],
              },
            ],
            comments: '',
          },
        };

        navigate(ROUTES.CREATE_COMPANY, {
          state: { initialData: normalizedData, companyPbId: selectedCompany.pbId },
        });
      }
    } catch (e) {
      handleApiError(e);
    }
  }, [
    getPitchbookCompanyDataByDealIdsQuery,
    getPitchbookCompanyDataQuery,
    getRoundSubrounds,
    handleApiError,
    navigate,
    selectedCompany,
    selectedCompanyMatchedRawDeals,
  ]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.container__heading}>
          <Typography color="var(--text-3-color)" variant="h4">
            Search
          </Typography>
          <div>
            <Button
              classes={{ startIcon: styles['container__new-button-icon'] }}
              color="secondary"
              size="small"
              startIcon={<AddIcon sx={{ width: '24px', height: '24px' }} />}
              sx={{ width: '203px', pl: '8px', pr: '8px', fontSize: '14px', mr: '8px' }}
              onClick={handleAddCompanyManually}
            >
              Add Company Manually
            </Button>
            <SearchInput
              initialValue={searchValue}
              placeholder="Search company in databases..."
              sx={SEARCH_INPUT_SX}
              onChange={handleSearchValueChange}
            />
          </div>
        </div>
        {searchPitchbookCompanyQuery.isFetching ? (
          <div className={styles.container__loading}>
            <CircularProgress />
          </div>
        ) : searchPitchbookCompanyQuery.currentData &&
          searchPitchbookCompanyQuery.currentData.result.data.length > 0 ? (
          <Companies
            companies={searchPitchbookCompanyQuery.currentData.result.data}
            currentPage={currentPage}
            perPageLimit={FETCH_LIMIT}
            totalCount={searchPitchbookCompanyQuery.currentData.result.total}
            onCompanyClick={showCompanyModal}
            onPageChange={handlePageChange}
          />
        ) : (
          searchValue.trim().length < 3 && (
            <div className={styles.container__search}>
              <Typography color="var(--text-3-color)" variant="subtitle1">
                Type something to search companies
              </Typography>
            </div>
          )
        )}
      </div>
      <CompanyModal
        data={selectedCompanyPitchbookData}
        loading={isLoadingGetPitchbookCompanyMutation}
        loadingPitchbookCompanyData={
          isFetchingGetPitchbookCompanyDataByDealIdsQuery || isFetchingGetPitchbookCompanyDataQuery
        }
        loadingPitchbookCompanyRawDeals={isFetchingGetPitchbookCompanyRawDealsQuery}
        visible={companyModalVisible}
        onClose={hideCompanyModal}
        onCompanyLoad={handleCompanyLoad}
        onCompanyProfileView={handleCompanyProfileView}
        onRoundsMatch={showMatchRoundsModal}
      />
      {matchRoundsModalVisible && (
        <MatchRoundsModal
          visible
          initialMatchedRawDeals={selectedCompanyMatchedRawDeals}
          rawDeals={selectedCompanyRawDeals}
          onClose={hideMatchRoundsModal}
          onSave={handleMatchRoundsSave}
        />
      )}
    </>
  );
};
